import { Cart, DiamondCartLine } from '@shared/lib/shopify/types/shopify-native';
import { safeFirst } from '@shared/lib/utils';
import { getDiamondTypesInCart } from './cart-utils';
import { rCarat } from './formats';

export function getCartFilters(cart: Cart | undefined, searchParams: URLSearchParams) {
  const filterByCart = searchParams.get('filterByCart');
  const diamondTypesInCart = getDiamondTypesInCart(cart);
  const canUseCartFilters = Object.keys(diamondTypesInCart).length > 0;
  const useCartFilters = canUseCartFilters && safeFirst(filterByCart) !== 'false';
  return {
    canUseCartFilters,
    useCartFilters,
    diamondTypesInCart,
  };
}

export function getCutFilters(cart: Cart | undefined, searchParams: URLSearchParams): string[] {
  const { useCartFilters, diamondTypesInCart } = getCartFilters(cart, searchParams);
  if (useCartFilters) {
    return diamondTypesInCart['Cut']?.map((cut) => cut.toLowerCase()) || [];
  } else {
    return searchParams.getAll('cut') || [];
  }
}

export function getRingFilters(searchParams: URLSearchParams): string[] {
  return searchParams.getAll('ring') || [];
}

export function getMetalFilters(searchParams: URLSearchParams): string[] {
  return searchParams.getAll('metal') || [];
}

export function getAllJewelryFilters(
  cart: Cart | undefined,
  searchParams: URLSearchParams
): Record<string, string[]> {
  return {
    cut: getCutFilters(cart, searchParams),
    ring: getRingFilters(searchParams),
    metal: getMetalFilters(searchParams),
  };
}

export function getAllJewelryFiltersAsArray(
  cart: Cart | undefined,
  searchParams: URLSearchParams
): { key: string; value: string }[] {
  return Object.entries(getAllJewelryFilters(cart, searchParams)).flatMap(([key, values]) => {
    return values.map((value) => ({ key, value: value }));
  });
}

export type AnnotatedDiamondLine = DiamondCartLine & {
  assigned: boolean;
  isDisabled: boolean;
  disabledReason: string;
};

function canSelectDiamond(
  supportedShapes: string[],
  supportedCarats: number[],
  carat: number,
  shape: string
): { canSelect: boolean; reason: string } {
  if (!supportedShapes.some((cut) => cut.toLowerCase() === shape?.toLowerCase())) {
    return {
      canSelect: false,
      reason: `Its ${shape!.toLowerCase()} shape won't fit this item.`,
    };
  }
  // If supportedCarats is empty, we don't need to check the weight
  if (
    supportedCarats.length > 0 &&
    !supportedCarats.some((value) => Math.abs(value - carat) < 0.2)
  ) {
    return {
      canSelect: false,
      reason: `Its ${rCarat(carat)} weight won't fit this item.`,
    };
  }
  return {
    canSelect: true,
    reason: '',
  };
}
export function getAnnotatedDiamondLines(
  diamondCartLines: DiamondCartLine[],
  supportedShapes: string[],
  supportedCarats: number[]
): AnnotatedDiamondLine[] {
  return diamondCartLines
    .map((line) => {
      const { canSelect, reason } = canSelectDiamond(
        supportedShapes,
        supportedCarats,
        line.merchandise.carat,
        line.merchandise.shape
      );
      return {
        ...line,
        assigned: !!line.jewelryLine,
        isDisabled: !canSelect || !!line.jewelryLine,
        disabledReason: !!line.jewelryLine ? "It's already set to another item" : reason,
      };
    })
    .sort((a, b) => {
      if (a.isDisabled !== b.isDisabled) {
        return a.isDisabled ? 1 : -1;
      }
      if (a.assigned !== b.assigned) {
        return a.assigned ? 1 : -1;
      }
      return 0;
    });
}

export function recommendInCartDiamond(
  supportedShapes: string[],
  supportedCarats: number[],
  diamondCartLines: DiamondCartLine[]
) {
  const recommendedDiamondLine = diamondCartLines.find((line) => {
    return canSelectDiamond(
      supportedShapes,
      supportedCarats,
      line.merchandise.carat,
      line.merchandise.shape
    ).canSelect;
  });
  return recommendedDiamondLine;
}
